import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, Wallet, Clock, ShieldCheck } from "lucide-react";
import { api, errMsg, store, setApiMode } from "../lib/api";
import { Logo, Loading, Emergency911 } from "../components/Bits";
import { BriefCard } from "../components/BriefCard";

export function Join() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/join/${token}`).then((r) => { setInfo(r.data); setF((x) => ({ ...x, name: r.data.helper_name })); }).catch(() => setInfo(false));
  }, [token]);
  if (info === null) return <Loading />;
  if (info === false) return <div className="p-8 text-lg">This invite link isn't valid anymore. Ask the family for a new one.</div>;
  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/join/${token}/accept`, f);
      store.setHelper(r.data);
      setApiMode("helper");
      nav("/helper");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const cg = info.caregiver_first || "A family";
  return (
    <div className="min-h-screen bg-hf-cream">
      <header className="mx-auto max-w-xl px-5 py-5"><Logo /></header>
      <main className="mx-auto max-w-xl space-y-6 px-5 pb-16">
        {info.is_demo && <p className="rounded-xl bg-hf-sun/30 px-4 py-2 text-sm">Demo invite for a fictional family.</p>}
        <div className="hf-rise">
          <p className="hf-eyebrow">You've been invited</p>
          <h1 data-testid="join-headline" className="mt-2 font-heading text-4xl font-semibold leading-tight">
            {cg} would love your help, {info.helper_name}.
          </h1>
          <p className="mt-4 text-xl text-hf-muted">
            {cg} cares for {info.recipient_name || "someone they love"} and is asking you to be their paid respite helper. That means a few hours here and there, so {cg} can rest.
          </p>
        </div>
        <div className="grid gap-3">
          {[
            [Wallet, "It's paid", "Visits are funded by a care program. You'd be paid for your time."],
            [HeartHandshake, "What you'd do", "Keep them company. Talk, share a snack, take a short walk. Non-medical only."],
            [Clock, "What's expected", "Show up when you say you will. Follow the brief and the agreement you write together."],
            [ShieldCheck, "Simple sign-up", "No app to download. No password. A short checklist and a few training cards."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="hf-card flex gap-4">
              <Icon className="h-8 w-8 shrink-0 text-hf-coral" />
              <div><p className="font-heading text-lg font-semibold">{t}</p><p className="text-lg text-hf-muted">{d}</p></div>
            </div>
          ))}
        </div>
        <div className="hf-card space-y-3">
          <label className="block text-base font-semibold">Your name</label>
          <input data-testid="join-name-input" className="hf-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <label className="block text-base font-semibold">Your email (for sign-in links)</label>
          <input data-testid="join-email-input" type="email" className="hf-input" placeholder="Optional" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <button data-testid="join-accept-button" disabled={busy || !f.name.trim()} onClick={accept} className="hf-btn hf-btn-primary w-full">Yes, I'd like to help</button>
          <p className="text-center text-base text-hf-muted">Not right now? That's okay. Just let {cg} know.</p>
        </div>
      </main>
    </div>
  );
}

export function PublicBrief() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/public/brief/${token}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [token]);
  if (d === null) return <Loading />;
  if (d === false) return <div className="p-8 text-lg">This brief isn't available.</div>;
  return (
    <div className="min-h-screen bg-hf-cream pb-32">
      <header className="mx-auto max-w-xl px-5 py-5"><Logo /></header>
      <main className="mx-auto max-w-xl space-y-4 px-5">
        {d.is_demo && <p className="rounded-xl bg-hf-sun/30 px-4 py-2 text-sm">Demo brief for a fictional family.</p>}
        <p data-testid="public-brief-from" className="text-lg text-hf-muted">From {d.caregiver_first}, approved for your visit.</p>
        <BriefCard brief={d.brief} recipient={d.recipient_name} />
      </main>
      <Emergency911 className="bottom-6" />
    </div>
  );
}

export function HelperLogin() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/helper/magic-link", { email });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <Logo />
      <h1 className="mt-8 font-heading text-3xl font-semibold">Get a sign-in link</h1>
      <p className="mt-3 text-lg text-hf-muted">No password. We'll email you a link that opens Handoff.</p>
      {sent ? (
        <p data-testid="helper-login-sent" className="mt-8 rounded-xl bg-hf-sage px-4 py-4 text-lg text-hf-green">If that email is on file, a link is on its way. Check your inbox.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          <input data-testid="helper-login-email" type="email" required className="hf-input" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="helper-login-submit" className="hf-btn hf-btn-primary w-full">Email me a link</button>
        </form>
      )}
      <Link to="/" className="mt-6 text-center text-base text-hf-muted underline">Back to Handoff</Link>
    </div>
  );
}

export function HelperVerify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/helper/verify", { token: params.get("token") }).then((r) => {
      store.setHelper(r.data);
      setApiMode("helper");
      nav("/helper", { replace: true });
    }).catch((e) => setErr(errMsg(e)));
  }, [params, nav]);
  return err ? (
    <div className="mx-auto max-w-md px-6 py-20 text-lg">
      <p data-testid="helper-verify-error">{err}</p>
      <Link to="/helper/login" className="hf-btn hf-btn-primary mt-6 w-full">Get a new link</Link>
    </div>
  ) : <Loading label="Signing you in..." />;
}
